import {
  Badge,
  Button,
  Callout,
  EmptyState,
  ErrorState,
  Heading,
  Modal,
  Search,
  Skeleton,
  Text,
} from "@imagedesignai/neo";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { GifmeApiClient, RunView, ShareLinkView } from "./api-client.ts";
import {
  formatBytes,
  groupLibraryRuns,
  type LibraryGroup,
  type LibraryOutput,
  type LibraryRun,
  mediaDimensionsLabel,
  preferredOutputDownload,
} from "./library.ts";
import type { MediaCache } from "./media-cache.ts";
import { CachedImage, OwnedImage } from "./media-image.tsx";
import { OutputDiagnostics } from "./output-diagnostics-modal.tsx";
import { ShareButton } from "./share-button.tsx";

export interface LibraryViewProps {
  api: GifmeApiClient;
  mediaCache: MediaCache;
  currentUserId: string;
  shareLinks: readonly ShareLinkView[];
  onPrepareShare(runId: string): Promise<ShareLinkView>;
  onShare(link: Promise<ShareLinkView>): Promise<void>;
  onRevokeLink(link: ShareLinkView): Promise<void>;
  onDownload(path: string, filename: string): Promise<void>;
}

function dateLabel(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));
}

function libraryRunsFrom(runs: readonly RunView[]): LibraryRun[] {
  return runs.map(
    (run): LibraryRun => ({
      id: run.id,
      ownerUserId: run.ownerUserId,
      ownerEmail: run.ownerEmail,
      state: run.state,
      createdAt: run.createdAt,
      generationSettingsContractVersion: run.generationSettingsContractVersion,
      generationSettings: run.generationSettings,
      sourceKey: run.sourceKey,
      source: run.source,
      sourcePreview: run.sourcePreview,
      sourceOriginalName: run.sourceOriginalName,
      sourceBytes: run.sourceBytes,
      outputBytes: run.outputBytes,
      failureCode: run.failureCode,
      error: run.error,
      media: run.media,
    }),
  );
}

function shareLinkFor(links: readonly ShareLinkView[], runId: string): ShareLinkView | null {
  return links.find((link) => link.runId === runId) ?? null;
}

function StatisticList({ entries }: { entries: ReadonlyArray<readonly [string, string]> }) {
  return (
    <dl className="library-stats">
      {entries.map(([term, description]) => (
        <div key={term} className="stat-row">
          <dt>{term}</dt>
          <dd>{description}</dd>
        </div>
      ))}
    </dl>
  );
}

function LibraryDetail({
  group,
  api,
  currentUserId,
  shareLinks,
  onClose,
  onPrepareShare,
  onShare,
  onRevokeLink,
  onDownload,
}: {
  group: LibraryGroup;
  api: GifmeApiClient;
  currentUserId: string;
  shareLinks: readonly ShareLinkView[];
  onClose(): void;
  onPrepareShare(runId: string): Promise<ShareLinkView>;
  onShare(link: Promise<ShareLinkView>): Promise<void>;
  onRevokeLink(link: ShareLinkView): Promise<void>;
  onDownload(path: string, filename: string): Promise<void>;
}) {
  const [sourceDimensions, setSourceDimensions] = useState("Loading…");
  const [diagnosticOutput, setDiagnosticOutput] = useState<LibraryOutput | null>(
    () => group.outputs.find((output) => output.outputMp4) ?? null,
  );
  const [isDiagnosticRequested, setIsDiagnosticRequested] = useState(false);
  const total = group.outputs.length + group.failures.length;

  return (
    <Modal className="library-detail-modal" title={group.sourceOriginalName} onClose={onClose}>
      <div className="library-detail">
        <div className="library-input-detail">
          <OwnedImage
            api={api}
            path={group.sourcePreview}
            alt={`Input GIF ${group.sourceOriginalName}`}
            onLoad={(event) =>
              setSourceDimensions(
                mediaDimensionsLabel(
                  event.currentTarget.naturalWidth,
                  event.currentTarget.naturalHeight,
                ),
              )
            }
          />
          <StatisticList
            entries={[
              ["File size", formatBytes(group.sourceBytes)],
              ["Dimensions", sourceDimensions],
              ["Attempts", String(total)],
              ["Successful", String(group.outputs.length)],
              ["Failed", String(group.failures.length)],
            ]}
          />
        </div>

        <section className="library-results">
          <Text kind="label" tone="secondary">
            Successful outputs ({group.outputs.length})
          </Text>
          <div className="library-output-grid">
            {group.outputs.map((output) => {
              const link = shareLinkFor(shareLinks, output.runId);
              const download = preferredOutputDownload(
                output.runId,
                output.output,
                output.outputMp4,
              );
              return (
                <article key={output.runId} className="library-output-card">
                  <OwnedImage
                    api={api}
                    path={output.output}
                    alt={`Output by ${output.ownerEmail}`}
                  />
                  <Text as="span" kind="micro" tone="muted" className="mono ellipsis">
                    {output.ownerEmail} · {dateLabel(output.createdAt)}
                  </Text>
                  <div className="library-output-actions">
                    <Button
                      intent="positive"
                      size="sm"
                      icon="download"
                      onClick={() => void onDownload(download.path, download.filename)}
                    >
                      Download
                    </Button>
                    {output.outputMp4 ? (
                      <Button
                        intent="secondary"
                        size="sm"
                        icon="search"
                        aria-pressed={diagnosticOutput?.runId === output.runId}
                        onClick={() => {
                          setDiagnosticOutput(output);
                          setIsDiagnosticRequested(true);
                        }}
                      >
                        Inspect diagnostics
                      </Button>
                    ) : null}
                    {output.ownerUserId === currentUserId ? (
                      <>
                        <ShareButton
                          link={link}
                          onPrepare={() => onPrepareShare(output.runId)}
                          onShare={onShare}
                        />
                        {link ? (
                          <Button
                            intent="danger"
                            size="sm"
                            icon="x"
                            onClick={() => void onRevokeLink(link)}
                          >
                            Revoke link
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {diagnosticOutput?.outputMp4 ? (
          <OutputDiagnostics
            key={diagnosticOutput.runId}
            api={api}
            runId={diagnosticOutput.runId}
            outputMp4={diagnosticOutput.outputMp4}
            isRequested={isDiagnosticRequested}
            generationSettingsContractVersion={diagnosticOutput.generationSettingsContractVersion}
            generationSettings={diagnosticOutput.generationSettings}
          />
        ) : null}

        {group.failures.length > 0 ? (
          <section className="library-failures">
            <Text kind="label" tone="secondary">
              Failed attempts ({group.failures.length})
            </Text>
            {group.failures.map((failure) => (
              <Callout key={failure.runId} intent="danger" label="Failed attempt">
                {failure.ownerEmail} · {dateLabel(failure.createdAt)} ·{" "}
                {failure.error || failure.failureCode || "Generation failed"}
              </Callout>
            ))}
          </section>
        ) : null}
      </div>
    </Modal>
  );
}

export function LibraryView({
  api,
  mediaCache,
  currentUserId,
  shareLinks,
  onPrepareShare,
  onShare,
  onRevokeLink,
  onDownload,
}: LibraryViewProps) {
  const [groups, setGroups] = useState<LibraryGroup[]>([]);
  const [selected, setSelected] = useState<LibraryGroup | null>(null);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = await api.json<{ runs: RunView[] }>("/api/library-runs");
      setGroups(groupLibraryRuns(libraryRunsFrom(payload.runs)));
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return groups;
    return groups.filter(
      (group) =>
        group.sourceOriginalName.toLowerCase().includes(needle) ||
        [...group.outputs, ...group.failures].some(({ ownerEmail }) =>
          ownerEmail.toLowerCase().includes(needle),
        ),
    );
  }, [groups, search]);

  return (
    <main className="library-layout">
      <div className="library-heading">
        <div>
          <Heading level={1} scale="md">
            Library
          </Heading>
          <Text kind="caption" tone="muted">
            Every source loop the team has cast, grouped by input GIF.
          </Text>
        </div>
        <div className="library-search">
          <Search
            label="Search the library"
            placeholder="Search by filename or caster"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </div>
      </div>
      {isLoading ? <Skeleton arrangement="card" label="Loading library" /> : null}
      {error ? (
        <ErrorState
          title="Could not load the library"
          description={error}
          action={
            <Button intent="secondary" size="sm" icon="refresh-cw" onClick={() => void load()}>
              Retry library
            </Button>
          }
          aria-live="polite"
        />
      ) : null}
      {!isLoading && !error && filtered.length === 0 ? (
        <EmptyState
          title={search ? "No matching GIFs" : "No successful runs yet"}
          description={search ? "Try another filename or caster." : undefined}
        />
      ) : null}
      <div className="library-grid">
        {filtered.map((group) => (
          <button
            key={group.id}
            type="button"
            className="library-card"
            onClick={() => setSelected(group)}
          >
            <CachedImage
              cache={mediaCache}
              path={group.sourcePreview}
              lazy
              alt={group.sourceOriginalName}
            />
            <Text as="span" kind="caption" tone="secondary" className="mono ellipsis">
              {group.sourceOriginalName}
            </Text>
            <span className="library-badges">
              <Badge intent="positive" tone="subtle" size="sm">
                {group.outputs.length} output{group.outputs.length === 1 ? "" : "s"}
              </Badge>
              <Badge
                intent={group.failures.length > 0 ? "danger" : "neutral"}
                tone="subtle"
                size="sm"
              >
                {group.failures.length} failed
              </Badge>
            </span>
          </button>
        ))}
      </div>
      {selected ? (
        <LibraryDetail
          group={selected}
          api={api}
          currentUserId={currentUserId}
          shareLinks={shareLinks}
          onClose={() => setSelected(null)}
          onPrepareShare={onPrepareShare}
          onShare={onShare}
          onRevokeLink={onRevokeLink}
          onDownload={onDownload}
        />
      ) : null}
    </main>
  );
}
